<template>
  <div class="suscripciones">
    <!-- Cabecera -->
    <div class="pagina-cabecera">
      <div>
        <h1 class="pagina-titulo">Suscripciones</h1>
        <p class="pagina-subtitulo">Gestiona el estado, plan y pagos de cada empresa.</p>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filtros-fila">
      <input v-model="filtros.busqueda" type="text" class="campo-busqueda" placeholder="Buscar empresa o RUC..." />
      <select v-model="filtros.estado" class="campo-select">
        <option value="">Todos los estados</option>
        <option value="activa">Activa</option>
        <option value="vencida">Vencida</option>
        <option value="suspendida">Suspendida</option>
        <option value="prueba">Prueba</option>
        <option value="cancelada">Cancelada</option>
      </select>
      <select v-model="filtros.ciclo" class="campo-select">
        <option value="">Todos los ciclos</option>
        <option value="mensual">Mensual</option>
        <option value="anual">Anual</option>
      </select>
      <button class="btn btn--secundario btn--xs" @click="cargarSuscripciones">
        <i class="bi bi-funnel"></i> Filtrar
      </button>
    </div>

    <!-- Metricas rapidas -->
    <div class="metricas-fila">
      <div v-for="m in metricas" :key="m.estado" class="metrica-chip" :class="'metrica-chip--' + m.estado">
        <span class="metrica-chip__valor">{{ m.total }}</span>
        <span class="metrica-chip__label">{{ m.estado }}</span>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="alerta alerta-error">
      <i class="bi bi-exclamation-triangle-fill"></i> {{ error }}
    </div>

    <!-- Tabla -->
    <div class="tabla-contenedor">
      <div v-if="cargando" class="texto-vacio"><i class="bi bi-arrow-clockwise spin"></i> Cargando...</div>
      <table v-else class="tabla">
        <thead>
          <tr>
            <th>Empresa / RUC</th>
            <th>Plan</th>
            <th>Ciclo</th>
            <th>Estado</th>
            <th>Vence</th>
            <th>Monto</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!suscripcionesFiltered.length">
            <td colspan="7" class="texto-vacio">Sin resultados</td>
          </tr>
          <tr v-for="s in suscripcionesFiltered" :key="s.ruc">
            <td>
              <div class="tabla__empresa">{{ s.razon_social }}</div>
              <div class="tabla__ruc">{{ s.ruc }}</div>
            </td>
            <td>
              <span class="plan-badge" :style="{ background: s.plan_color || 'var(--primary-main)' }">
                {{ s.plan_nombre || 'Sin plan' }}
              </span>
            </td>
            <td>{{ s.ciclo_pago }}</td>
            <td>
              <span class="estado-chip" :class="estadoCss(s.estado)">{{ s.estado }}</span>
            </td>
            <td>
              <span :class="venceCss(s.dias_restantes)">
                {{ s.fecha_fin }}
                <small v-if="s.dias_restantes != null">({{ s.dias_restantes }}d)</small>
              </span>
            </td>
            <td class="tabla__monto">${{ formatear(s.monto) }}</td>
            <td>
              <div class="acciones-fila">
                <button class="btn-icono" title="Asignar / cambiar plan" @click="abrirAsignarPlan(s)">
                  <i class="bi bi-tags"></i>
                </button>
                <button class="btn-icono" title="Conceder prorroga" @click="abrirProrroga(s)">
                  <i class="bi bi-calendar-plus"></i>
                </button>
                <button class="btn-icono" title="Modulos activos" @click="abrirModulosEmpresa(s)">
                  <i class="bi bi-grid-3x3-gap"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ═══ MODAL: Asignar plan ═══ -->
    <div v-if="modalPlan.visible" class="modal-overlay" @click.self="modalPlan.visible = false">
      <div class="modal-contenedor">
        <div class="modal-cabecera">
          <div>
            <h3>Asignar Plan</h3>
            <p class="modal-subtitulo">{{ modalPlan.empresa }}</p>
          </div>
          <button class="btn-icono" @click="modalPlan.visible = false"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-cuerpo">
          <div class="campo">
            <label>Plan</label>
            <select v-model="modalPlan.plan_id" class="campo-select-full">
              <option value="">Selecciona un plan...</option>
              <option v-for="p in planesDisponibles" :key="p.id" :value="p.id">
                {{ p.nombre }} — ${{ formatear(p.precio_mensual) }}/mes / ${{ formatear(p.precio_anual) }}/ano
              </option>
            </select>
          </div>
          <div class="campo">
            <label>Ciclo de pago</label>
            <select v-model="modalPlan.ciclo" class="campo-select-full">
              <option value="mensual">Mensual</option>
              <option value="anual">Anual</option>
            </select>
          </div>
          <div class="alerta-info" v-if="modalPlan.plan_id">
            <i class="bi bi-info-circle"></i>
            Esta accion reemplaza el plan actual y recalcula los modulos habilitados para la empresa.
          </div>
        </div>
        <div class="modal-pie">
          <button class="btn btn--secundario" @click="modalPlan.visible = false">Cancelar</button>
          <button class="btn btn--primario" :disabled="!modalPlan.plan_id || modalPlan.guardando" @click="guardarAsignarPlan">
            <span v-if="modalPlan.guardando"><i class="bi bi-arrow-clockwise spin"></i> Asignando...</span>
            <span v-else>Asignar plan</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══ MODAL: Prorroga / compromiso de pago ═══ -->
    <div v-if="modalProrroga.visible" class="modal-overlay" @click.self="modalProrroga.visible = false">
      <div class="modal-contenedor">
        <div class="modal-cabecera">
          <div>
            <h3>Conceder Prorroga</h3>
            <p class="modal-subtitulo">{{ modalProrroga.empresa }}</p>
          </div>
          <button class="btn-icono" @click="modalProrroga.visible = false"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-cuerpo">
          <div class="campo">
            <label>Comprometido hasta *</label>
            <input v-model="modalProrroga.hasta" type="date" />
          </div>
          <div class="campo">
            <label>Nota / motivo</label>
            <textarea v-model="modalProrroga.nota" rows="3" placeholder="Motivo de la prorroga..."></textarea>
          </div>
        </div>
        <div class="modal-pie">
          <button class="btn btn--secundario" @click="modalProrroga.visible = false">Cancelar</button>
          <button class="btn btn--primario" :disabled="!modalProrroga.hasta || modalProrroga.guardando" @click="guardarProrroga">
            <span v-if="modalProrroga.guardando"><i class="bi bi-arrow-clockwise spin"></i> Guardando...</span>
            <span v-else>Confirmar prorroga</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══ MODAL: Modulos empresa ═══ -->
    <div v-if="modalModulos.visible" class="modal-overlay" @click.self="modalModulos.visible = false">
      <div class="modal-contenedor modal-contenedor--ancho">
        <div class="modal-cabecera">
          <div>
            <h3>Modulos activos</h3>
            <p class="modal-subtitulo">{{ modalModulos.empresa }}</p>
          </div>
          <button class="btn-icono" @click="modalModulos.visible = false"><i class="bi bi-x-lg"></i></button>
        </div>
        <div v-if="modalModulos.cargando" class="modal-cuerpo modal-cuerpo--centrado">
          <i class="bi bi-arrow-clockwise spin grande"></i> Cargando...
        </div>
        <div v-else class="modal-cuerpo">
          <p class="info-modulos">Los modulos marcados provienen del plan asignado. Los overrides son ajustes manuales.</p>
          <div v-for="principal in modalModulos.modulos" :key="principal.id" class="modulo-seccion">
            <div class="modulo-seccion__cab">
              <i :class="iconoModulo(principal.ruta_modulo)"></i>
              {{ principal.nombre_modulo }}
            </div>
            <div class="modulo-seccion__items">
              <label v-for="sec in principal.secundarios" :key="sec.id" class="modulo-item" :class="{ 'modulo-item--override': sec.es_override }">
                <input type="checkbox" :checked="sec.habilitado" @change="toggleModulo(sec, $event.target.checked)" />
                <span>{{ sec.nombre }}</span>
                <span v-if="sec.es_override" class="modulo-tag modulo-tag--override">override</span>
                <span v-else-if="sec.habilitado" class="modulo-tag modulo-tag--plan">plan</span>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-pie">
          <button class="btn btn--secundario" @click="modalModulos.visible = false">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

const ICONOS_MODULO = {
  dashboard: 'bi bi-house-door', ventas: 'bi bi-receipt', pedidos: 'bi bi-bag-check',
  proformas: 'bi bi-file-earmark-text', clientes: 'bi bi-people', inventario: 'bi bi-box-seam',
  compras: 'bi bi-bag', caja: 'bi bi-cash-stack', reportes: 'bi bi-bar-chart',
  configuracion: 'bi bi-gear', contabilidad: 'bi bi-calculator',
}

export default {
  name: 'SuscripcionesList',
  data() {
    return {
      cargando: false, error: null,
      suscripciones: [], metricas: [], planesDisponibles: [],
      filtros: { busqueda: '', estado: '', ciclo: '' },
      modalPlan: { visible: false, ruc: '', empresa: '', plan_id: '', ciclo: 'mensual', guardando: false },
      modalProrroga: { visible: false, ruc: '', empresa: '', hasta: '', nota: '', guardando: false },
      modalModulos: { visible: false, ruc: '', empresa: '', modulos: [], cargando: false },
    }
  },
  computed: {
    suscripcionesFiltered() {
      return this.suscripciones.filter(s => {
        const b = this.filtros.busqueda.toLowerCase()
        const matchBusqueda = !b || s.ruc.includes(b) || (s.razon_social || '').toLowerCase().includes(b)
        const matchEstado   = !this.filtros.estado || s.estado === this.filtros.estado
        const matchCiclo    = !this.filtros.ciclo  || s.ciclo_pago === this.filtros.ciclo
        return matchBusqueda && matchEstado && matchCiclo
      })
    },
  },
  mounted() { this.cargarSuscripciones(); this.cargarPlanes() },
  methods: {
    async cargarSuscripciones() {
      this.cargando = true; this.error = null
      try {
        const res = await api.get('/admin/suscripciones')
        this.suscripciones = res.data?.datos || []
        this.calcularMetricas()
      } catch (e) {
        this.error = 'Error al cargar suscripciones: ' + (e.response?.data?.mensaje || e.message)
      } finally { this.cargando = false }
    },
    async cargarPlanes() {
      try {
        const res = await api.get('/admin/planes')
        this.planesDisponibles = (res.data?.datos || []).filter(p => p.activo)
      } catch (e) {
        // planes no criticos, se ignora el error
      }
    },
    calcularMetricas() {
      const conteo = {}
      for (const s of this.suscripciones) {
        conteo[s.estado] = (conteo[s.estado] || 0) + 1
      }
      this.metricas = Object.entries(conteo).map(([estado, total]) => ({ estado, total }))
    },
    // Asignar plan
    abrirAsignarPlan(s) {
      this.modalPlan.ruc     = s.ruc
      this.modalPlan.empresa = s.razon_social
      this.modalPlan.plan_id = s.plan_id || ''
      this.modalPlan.ciclo   = s.ciclo_pago || 'mensual'
      this.modalPlan.visible = true
    },
    async guardarAsignarPlan() {
      this.modalPlan.guardando = true
      try {
        await api.post(`/admin/empresas/${this.modalPlan.ruc}/asignar-plan`, {
          plan_id: this.modalPlan.plan_id,
          ciclo:   this.modalPlan.ciclo,
        })
        await this.cargarSuscripciones()
        this.modalPlan.visible = false
      } catch (e) {
        alert('Error al asignar plan: ' + (e.response?.data?.mensaje || e.message))
      } finally { this.modalPlan.guardando = false }
    },
    // Prorroga
    abrirProrroga(s) {
      this.modalProrroga.ruc     = s.ruc
      this.modalProrroga.empresa = s.razon_social
      this.modalProrroga.hasta   = s.compromiso_pago_hasta || ''
      this.modalProrroga.nota    = s.compromiso_nota || ''
      this.modalProrroga.visible = true
    },
    async guardarProrroga() {
      this.modalProrroga.guardando = true
      try {
        await api.post(`/admin/empresas/${this.modalProrroga.ruc}/compromiso-pago`, {
          hasta: this.modalProrroga.hasta,
          nota:  this.modalProrroga.nota,
        })
        await this.cargarSuscripciones()
        this.modalProrroga.visible = false
      } catch (e) {
        alert('Error al guardar prorroga: ' + (e.response?.data?.mensaje || e.message))
      } finally { this.modalProrroga.guardando = false }
    },
    // Modulos empresa
    async abrirModulosEmpresa(s) {
      this.modalModulos.ruc     = s.ruc
      this.modalModulos.empresa = s.razon_social
      this.modalModulos.visible = true
      this.modalModulos.cargando= true
      this.modalModulos.modulos = []
      try {
        const res = await api.get(`/admin/empresas/${s.ruc}/modulos-plan`)
        this.modalModulos.modulos = res.data?.datos || []
      } catch (e) { alert('Error al cargar modulos.') }
      finally { this.modalModulos.cargando = false }
    },
    async toggleModulo(sec, habilitado) {
      try {
        await api.put(`/admin/empresas/${this.modalModulos.ruc}/modulos/${sec.id}/toggle`, { habilitado: habilitado ? 1 : 0 })
        sec.habilitado = habilitado
        sec.es_override = true
      } catch (e) { alert('Error al cambiar modulo.') }
    },
    // Helpers
    estadoCss(estado) {
      const m = { activa: 'chip--verde', vencida: 'chip--rojo', suspendida: 'chip--gris', prueba: 'chip--azul', cancelada: 'chip--gris' }
      return m[estado] || 'chip--gris'
    },
    venceCss(dias) {
      if (dias == null) return ''
      if (dias <= 3)  return 'texto-rojo'
      if (dias <= 7)  return 'texto-naranja'
      if (dias <= 15) return 'texto-amarillo'
      return ''
    },
    iconoModulo(codigo) { return ICONOS_MODULO[codigo] || 'bi bi-puzzle' },
    formatear(v) { return parseFloat(v || 0).toFixed(2) },
  },
}
</script>

<style scoped>
.suscripciones   { padding:1.5rem; max-width:1400px; margin:0 auto; display:flex; flex-direction:column; gap:1.25rem; }
.pagina-cabecera { display:flex; align-items:flex-start; justify-content:space-between; }
.pagina-titulo   { font-size:1.5rem; font-weight:700; color:var(--text-primary); margin:0 0 .25rem; }
.pagina-subtitulo{ font-size:.875rem; color:var(--text-secondary); margin:0; }
.alerta          { padding:.75rem 1rem; border-radius:.5rem; display:flex; gap:.5rem; }
.alerta-error    { background:color-mix(in srgb,var(--accent-danger) 10%,transparent); color:var(--accent-danger); border:1px solid color-mix(in srgb,var(--accent-danger) 30%,transparent); }
.alerta-info     { background:color-mix(in srgb,#0ea5e9 10%,transparent); color:#0ea5e9; padding:.6rem .75rem; border-radius:.375rem; font-size:.8rem; display:flex; gap:.5rem; }
/* Filtros */
.filtros-fila    { display:flex; gap:.75rem; flex-wrap:wrap; }
.campo-busqueda,.campo-select,.campo-select-full { background:var(--bg-main); border:1px solid var(--border-main); border-radius:.375rem; padding:.5rem .75rem; color:var(--text-primary); font-size:.875rem; }
.campo-busqueda  { flex:1; min-width:200px; }
.campo-select    { min-width:160px; }
.campo-select-full { width:100%; }
/* Metricas */
.metricas-fila   { display:flex; gap:.5rem; flex-wrap:wrap; }
.metrica-chip    { display:flex; align-items:center; gap:.4rem; padding:.35rem .75rem; border-radius:999px; font-size:.78rem; font-weight:600; background:var(--border-main); color:var(--text-secondary); }
.metrica-chip__valor { font-size:1rem; font-weight:700; color:var(--text-primary); }
.metrica-chip--activa    { background:color-mix(in srgb,var(--accent-success) 15%,transparent); color:var(--accent-success); }
.metrica-chip--activa .metrica-chip__valor { color:var(--accent-success); }
.metrica-chip--vencida   { background:color-mix(in srgb,var(--accent-danger) 15%,transparent); color:var(--accent-danger); }
.metrica-chip--suspendida{ background:color-mix(in srgb,var(--text-muted) 15%,transparent); color:var(--text-muted); }
/* Tabla */
.tabla-contenedor{ background:var(--secondary-bg-main); border:1px solid var(--border-main); border-radius:.75rem; overflow:auto; }
.tabla           { width:100%; border-collapse:collapse; font-size:.825rem; }
.tabla th        { text-align:left; padding:.6rem .75rem; font-size:.75rem; font-weight:600; color:var(--text-secondary); border-bottom:1px solid var(--border-main); white-space:nowrap; }
.tabla td        { padding:.65rem .75rem; border-bottom:1px solid var(--border-main); color:var(--text-primary); vertical-align:middle; }
.tabla tr:last-child td { border-bottom:none; }
.tabla__empresa  { font-weight:600; }
.tabla__ruc      { font-size:.7rem; color:var(--text-muted); }
.tabla__monto    { font-weight:700; color:var(--accent-success); }
.plan-badge      { font-size:.7rem; font-weight:700; color:#fff; padding:.2rem .5rem; border-radius:.25rem; white-space:nowrap; }
.estado-chip     { font-size:.72rem; font-weight:600; padding:.15rem .5rem; border-radius:999px; }
.chip--verde { background:color-mix(in srgb,var(--accent-success) 15%,transparent); color:var(--accent-success); }
.chip--rojo  { background:color-mix(in srgb,var(--accent-danger) 15%,transparent); color:var(--accent-danger); }
.chip--amarillo{ background:color-mix(in srgb,var(--accent-warning) 15%,transparent); color:var(--accent-warning); }
.chip--gris  { background:color-mix(in srgb,var(--text-muted) 15%,transparent); color:var(--text-muted); }
.chip--azul  { background:color-mix(in srgb,#0ea5e9 15%,transparent); color:#0ea5e9; }
.texto-rojo    { color:var(--accent-danger); font-weight:600; }
.texto-naranja { color:#f97316; font-weight:600; }
.texto-amarillo{ color:var(--accent-warning); font-weight:600; }
.texto-vacio   { text-align:center; color:var(--text-muted); padding:2rem; font-size:.875rem; }
.acciones-fila { display:flex; gap:.25rem; }
/* Botones */
.btn           { display:inline-flex; align-items:center; gap:.4rem; border:none; border-radius:.5rem; padding:.6rem 1.2rem; cursor:pointer; font-size:.875rem; font-weight:600; transition:all .15s; }
.btn:disabled  { opacity:.5; cursor:not-allowed; }
.btn--primario   { background:var(--primary-main); color:#fff; }
.btn--primario:hover { background:var(--primary-hover); }
.btn--secundario { background:var(--bg-main); color:var(--text-primary); border:1px solid var(--border-main); }
.btn--secundario:hover { background:var(--border-main); }
.btn--xs       { padding:.3rem .75rem; font-size:.8rem; }
.btn-icono     { background:none; border:1px solid var(--border-main); border-radius:.375rem; padding:.3rem .5rem; cursor:pointer; color:var(--text-secondary); transition:all .15s; }
.btn-icono:hover { background:var(--bg-main); color:var(--text-primary); }
/* Modales */
.modal-overlay   { position:fixed; inset:0; background:rgba(0,0,0,.5); display:flex; align-items:center; justify-content:center; z-index:1000; padding:1rem; }
.modal-contenedor{ background:var(--secondary-bg-main); border-radius:.75rem; width:100%; max-width:520px; max-height:90vh; display:flex; flex-direction:column; box-shadow:var(--shadow-md); }
.modal-contenedor--ancho { max-width:860px; }
.modal-cabecera  { padding:1.25rem 1.5rem; border-bottom:1px solid var(--border-main); display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; }
.modal-cabecera h3 { margin:0; font-size:1.1rem; font-weight:700; color:var(--text-primary); }
.modal-subtitulo { font-size:.8rem; color:var(--text-secondary); margin:.25rem 0 0; }
.modal-cuerpo    { padding:1.5rem; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:1rem; }
.modal-cuerpo--centrado { align-items:center; justify-content:center; gap:.75rem; color:var(--text-secondary); }
.modal-pie       { padding:1rem 1.5rem; border-top:1px solid var(--border-main); display:flex; justify-content:flex-end; gap:.75rem; }
.campo           { display:flex; flex-direction:column; gap:.35rem; }
.campo label     { font-size:.8rem; font-weight:600; color:var(--text-secondary); }
.campo input,.campo textarea { background:var(--bg-main); border:1px solid var(--border-main); border-radius:.375rem; padding:.5rem .75rem; color:var(--text-primary); font-size:.875rem; width:100%; }
.campo input:focus,.campo textarea:focus { outline:none; border-color:var(--border-focus); }
/* Modulos */
.info-modulos    { font-size:.8rem; color:var(--text-secondary); margin:0; padding:.5rem; background:var(--bg-main); border-radius:.375rem; }
.modulo-seccion  { border:1px solid var(--border-main); border-radius:.5rem; overflow:hidden; }
.modulo-seccion__cab { display:flex; align-items:center; gap:.5rem; padding:.6rem 1rem; background:var(--bg-main); font-weight:700; font-size:.85rem; color:var(--text-primary); }
.modulo-seccion__items { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); }
.modulo-item     { display:flex; align-items:center; gap:.5rem; padding:.5rem 1rem; cursor:pointer; border-top:1px solid var(--border-main); font-size:.825rem; transition:background .1s; }
.modulo-item:hover { background:var(--bg-main); }
.modulo-item span { flex:1; color:var(--text-primary); }
.modulo-tag      { font-size:.65rem; font-weight:700; padding:.1rem .35rem; border-radius:.25rem; }
.modulo-tag--plan { background:color-mix(in srgb,#0ea5e9 12%,transparent); color:#0ea5e9; }
.modulo-tag--override { background:color-mix(in srgb,var(--accent-warning) 12%,transparent); color:var(--accent-warning); }
/* Animaciones */
.spin { animation:rotar 1s linear infinite; display:inline-block; }
@keyframes rotar { to { transform:rotate(360deg); } }
.grande { font-size:2rem; }
@media (max-width:768px) {
  .filtros-fila { flex-direction:column; }
  .tabla th:nth-child(3),.tabla td:nth-child(3),
  .tabla th:nth-child(6),.tabla td:nth-child(6) { display:none; }
}
</style>
